// ./src/components/features/Coupons/CouponRedeemPlacesEditor/CouponRedeemPlacesEditor.tsx
//
// "Drink token → Bar, Meal → Food truck" — the voucher types of an event that are redeemed somewhere OTHER than the
// event's default place (owner, 2026-10-25). One row per voucher type; stored in `event.settings.coupons.redeemAt`.
//
// ⚠ It is a sibling of `CouponGrantRulesEditor`, not a mode of it: same row shape (one header row of labels, a trash
// icon lined up with the inputs) because it sits in the same drawer, but a different question with different columns.
// Fully controlled: the drawer owns the rows and saves them.

import React from "react";
import { ActionIcon, Button, Grid, Skeleton, Stack, Text, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { AppIcon, SelectField } from "@/components/generic";

/** One editable row. `key` is a stable client id — a new row has no value yet. */
export interface RedeemPlaceRow {
  key: string;
  couponTypeId: string;
  servicePointId: string;
}

export interface CouponRedeemPlacesEditorProps {
  rows: RedeemPlaceRow[];
  onChange: (rows: RedeemPlaceRow[]) => void;
  /** The voucher types usable at this event. */
  typeOptions: { value: string; label: string }[];
  /** The event's live points (its venues' and its own). */
  pointOptions: { value: string; label: string }[];
  /** Skeleton rows while the options load. */
  loading?: boolean;
  disabled?: boolean;
  /** A presence problem on row i, by column — drawn on the control it is about. */
  rowErrors?: Record<number, Partial<Record<"couponTypeId" | "servicePointId", string>>>;
  /** A refusal about the list as a whole (the server's `couponRedeemAt`). */
  error?: string | null;
}

const newKey = (): string =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `row-${Date.now()}-${Math.random()}`;

export const emptyRedeemPlaceRow = (): RedeemPlaceRow => ({ key: newKey(), couponTypeId: "", servicePointId: "" });

const CouponRedeemPlacesEditorComponent: React.FC<CouponRedeemPlacesEditorProps> = ({
  rows,
  onChange,
  typeOptions,
  pointOptions,
  loading = false,
  disabled = false,
  rowErrors,
  error,
}) => {
  const { t } = useTranslation(["coupons", "common"]);

  const updateAt = (index: number, patch: Partial<RedeemPlaceRow>) => {
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const removeAt = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const voucherLabel = t("coupons:places.columns.voucher", { defaultValue: "Voucher" });
  const placeLabel = t("coupons:places.columns.place", { defaultValue: "Redeemed at" });

  // Column labels in ONE header row — on the first row's inputs they make it taller than the rest.
  const renderHeader = () => (
    <Grid gutter="xs" align="center" px={2}>
      <Grid.Col span={6}>
        <Text size="md" c="dimmed" fw={600}>
          {voucherLabel}
        </Text>
      </Grid.Col>
      <Grid.Col span={5}>
        <Text size="md" c="dimmed" fw={600}>
          {placeLabel}
        </Text>
      </Grid.Col>
      <Grid.Col span={1} />
    </Grid>
  );

  if (loading) {
    // Matches the real row's shape, so nothing jumps when the options arrive.
    return (
      <Stack gap="md">
        {renderHeader()}
        {Array.from({ length: Math.min(Math.max(rows.length, 1), 4) }, (_, i) => (
          <Grid key={`sk-${i}`} gutter="xs" align="center">
            <Grid.Col span={6}>
              <Skeleton height={42} radius="sm" />
            </Grid.Col>
            <Grid.Col span={5}>
              <Skeleton height={42} radius="sm" />
            </Grid.Col>
            <Grid.Col span={1}>
              <Skeleton height={28} circle />
            </Grid.Col>
          </Grid>
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {rows.length > 0 ? renderHeader() : null}

      {rows.map((row, index) => {
        const rowError = rowErrors?.[index];
        // Top-aligned once a message appears under one control, or its row-mates slide down.
        const hasRowError = !!rowError && Object.values(rowError).some(Boolean);
        // ⚠ A type another row already names is not offered again — one place per voucher type.
        const takenElsewhere = new Set(rows.filter((_, i) => i !== index).map((r) => r.couponTypeId).filter(Boolean));

        return (
          <Grid key={row.key} gutter="xs" align={hasRowError ? "flex-start" : "center"}>
            <Grid.Col span={6}>
              <SelectField
                aria-label={voucherLabel}
                placeholder={t("coupons:rules.pickVoucher", { defaultValue: "Pick a voucher" })}
                data={typeOptions.filter((o) => o.value === row.couponTypeId || !takenElsewhere.has(o.value))}
                value={row.couponTypeId || null}
                onChange={(_name, v) => updateAt(index, { couponTypeId: v ?? "" })}
                disabled={disabled}
                searchable
                comboboxProps={DRAWER_COMBOBOX_PROPS}
                size="md"
                error={rowError?.couponTypeId}
              />
            </Grid.Col>
            <Grid.Col span={5}>
              <SelectField
                aria-label={placeLabel}
                placeholder={t("coupons:places.pickPlace", { defaultValue: "Pick a place" })}
                data={pointOptions}
                value={row.servicePointId || null}
                onChange={(_name, v) => updateAt(index, { servicePointId: v ?? "" })}
                disabled={disabled}
                searchable
                comboboxProps={DRAWER_COMBOBOX_PROPS}
                size="md"
                error={rowError?.servicePointId}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Tooltip label={t("common:buttons.remove", { defaultValue: "Remove" })} withArrow position="top">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => removeAt(index)}
                  disabled={disabled}
                  aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
                >
                  <AppIcon iconName="Trash" size={16} />
                </ActionIcon>
              </Tooltip>
            </Grid.Col>
          </Grid>
        );
      })}

      {/* A refusal about the list as a whole belongs next to the rows, not only in the footer. */}
      {error ? (
        <Text size="md" c="red">
          {error}
        </Text>
      ) : null}

      <Button
        variant="light"
        size="md"
        onClick={() => onChange([...rows, emptyRedeemPlaceRow()])}
        disabled={disabled || rows.length >= typeOptions.length}
        leftSection={<AppIcon iconName="Plus" size={14} />}
        style={{ alignSelf: "flex-start" }}
      >
        {t("coupons:places.add", { defaultValue: "A voucher redeemed somewhere else" })}
      </Button>
    </Stack>
  );
};

export const CouponRedeemPlacesEditor = React.memo(CouponRedeemPlacesEditorComponent);
